import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) return <div>Loading...</div>;

  // All modules available to all users
  const modules = [
    {
      title: 'Onboarding',
      description: 'Complete the Onboarding process. Here, you\'ll find everything you need to get started from completing your joining formalities and uploading documents to exploring policies, benefits, and company culture.',
      icon: <PersonAddIcon color="primary" sx={{ fontSize: 40 }} />,
      path: '/onboarding',
    },
    {
      title: 'Existing Employee',
      description: 'This section is designed to manage your documents, access training resources, and share feedback all in one place. Stay updated, enhance your skills, and help us improve together.',
      icon: <PersonIcon color="primary" sx={{ fontSize: 40 }} />,
      path: '/existing',
    },
    {
      title: 'Offboarding',
      description: 'We sincerely hope you never feel the need to use this section. This section is designed to ensure smooth offboarding process including Asset return, Exit Formalities and Declaration.',
      icon: <PersonRemoveIcon color="primary" sx={{ fontSize: 40 }} />,
      path: '/offboarding',
    },
  ];

  return (
    <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, mt: 2 }}>
        <Typography gutterBottom sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: 1, fontSize: 30 }}>
          Welcome, {user.firstName} {user.lastName}, to Divergent Insights!
        </Typography>
        <Typography color="text.secondary" gutterBottom sx={{ fontStyle: 'italic', fontWeight: 400, mb: 3, fontSize: 20 }}>
          Creating Value Through Collaboration
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, fontSize: 14, mb: 3, lineHeight: 1.5 }}>
          Hello Employee,<br />
          Welcome to Divergent Insights! You can access all available modules to manage your documents, training, and other employee resources.
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, fontSize: 14, mb: 3, fontWeight: 600, color: 'text.secondary', letterSpacing: 0.5, fontStyle: 'italic' }}>
          Select a module below to get started
        </Typography>
      </Box>

      <Grid container spacing={4} alignItems="stretch">
        {modules.map((module) => (
          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            key={module.title}
            sx={{ display: 'flex', minWidth: { xs: '100%', sm: '100%', md: 320 } }}
          >
            <Card
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                minHeight: 360,
                borderRadius: 3,
                boxShadow: 2,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-6px) scale(1.03)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: 'rgba(126,87,194,0.12)',
                    mb: 2,
                  }}
                >
                  {React.cloneElement(module.icon, { sx: { fontSize: 44, color: '#7e57c2' } })}
                </Box>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3, fontSize: 20 }}>
                  {module.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, fontSize: 14, lineHeight: 1.6 }}
                >
                  {module.description}
                </Typography>
              </CardContent>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(module.path)}
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  borderRadius: 2,
                  fontSize: 14,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  alignSelf: 'center',
                  mb: 2,
                }}
              >
                {module.title === 'Existing Employee'
                  ? 'View Existing Employee'
                  : `Start ${module.title}`}
              </Button>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Dashboard;